/* eslint-disable @typescript-eslint/no-explicit-any */

import { ReactNode, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { buildKindredExternalShareText, encodeKindredShareContent, KindredSharePayload } from "@/lib/kindredShare";
import { dispatchNotification } from "@/lib/pushNotifications";
import { toast } from "sonner";
import { sortChannelsByRecentActivity } from "../../shared/community";
import { applyChannelMembershipState, getJoinedChannels } from "../../shared/community";
import { AppBottomSheetSurface, AppButton, AppCard, AppCardBody, AppFieldGroup, AppIconButton, AppSection, AppSectionHeader, AppText, AppTextArea } from "../../ui-foundation";

type TriggerTone = "primary" | "secondary" | "outline" | "ghost";
type TriggerSize = "sm" | "md";

interface KindredShareSheetProps {
  payload: KindredSharePayload;
  triggerLabel?: string;
  triggerContent?: ReactNode;
  triggerTone?: TriggerTone;
  triggerSize?: TriggerSize;
  iconOnly?: boolean;
}

const KindredShareSheet = ({
  payload,
  triggerLabel = "Share inside Kindred",
  triggerContent,
  triggerTone = "outline",
  triggerSize = "sm",
  iconOnly = false,
}: KindredShareSheetProps) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [channels, setChannels] = useState<any[]>([]);
  const [conversations, setConversations] = useState<any[]>([]);
  const [rooms, setRooms] = useState<any[]>([]);
  const [note, setNote] = useState("");

  useEffect(() => {
    if (!open || !user?.id) return;

    let active = true;

    const load = async () => {
      setLoading(true);

      const [{ data: nextChannels }, { data: connections }, { data: memberships }, { data: channelActivityPosts }] = await Promise.all([
        supabase.from("channels").select("id, name, emoji, description, sort_order, is_default").order("sort_order", { ascending: true }),
        supabase
          .from("connections")
          .select("*")
          .eq("status", "connected")
          .or(`requester_id.eq.${user.id},recipient_id.eq.${user.id}`),
        supabase.from("channel_members").select("channel_id, joined_at").eq("user_id", user.id),
        supabase.from("posts").select("channel_id, created_at"),
      ]);

      if (!active) return;

      const nextConversations = await Promise.all(
        (connections ?? []).map(async (connection: any) => {
          const otherId = connection.requester_id === user.id ? connection.recipient_id : connection.requester_id;
          const { data: profile } = await supabase
            .from("profiles")
            .select("display_name")
            .eq("id", otherId)
            .single();

          return {
            id: connection.id,
            label: profile?.display_name ?? "Member",
          };
        }),
      );

      const { data: roomMemberships } = await supabase.from("chat_room_members" as any).select("room_id").eq("user_id", user.id);
      const roomIds = (roomMemberships ?? []).map((item: any) => item.room_id);
      const { data: nextRooms } = roomIds.length
        ? await supabase.from("chat_rooms" as any).select("id, name, description").in("id", roomIds)
        : { data: [] as any[] };

      const joinedChannels = getJoinedChannels(
        applyChannelMembershipState(
          sortChannelsByRecentActivity(nextChannels ?? [], channelActivityPosts ?? []),
          memberships ?? [],
        ),
      );

      setChannels(joinedChannels);
      setConversations(nextConversations);
      setRooms(nextRooms ?? []);
      setLoading(false);
    };

    void load();

    return () => {
      active = false;
    };
  }, [open, user?.id]);

  useEffect(() => {
    if (!open) setNote("");
  }, [open]);

  const shareToKinfeed = () => {
    navigate("/community/feed/new", { state: { sharePayload: payload, prefill: note.trim() } });
    setOpen(false);
  };

  const shareOutsideKindred = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: payload.title,
          text: [payload.title, payload.subtitle].filter(Boolean).join("\n"),
          url: payload.externalUrl,
        });
      } else {
        await navigator.clipboard.writeText(payload.externalUrl);
        toast.success("Link copied");
      }
      setOpen(false);
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;

      try {
        await navigator.clipboard.writeText(buildKindredExternalShareText(payload));
        toast.success("Link copied");
        setOpen(false);
      } catch {
        toast.error("Could not open sharing. Please try again.");
      }
    }
  };

  const shareToConversation = async (connectionId: string) => {
    if (!user?.id) return;

    const content = encodeKindredShareContent(note, payload);
    await supabase.from("direct_messages").insert({
      connection_id: connectionId,
      sender_id: user.id,
      content,
    } as any);

    const [{ data: connection }, { data: profile }] = await Promise.all([
      supabase
        .from("connections")
        .select("requester_id, recipient_id")
        .eq("id", connectionId)
        .maybeSingle(),
      supabase.from("profiles").select("display_name").eq("id", user.id).maybeSingle(),
    ]);

    const recipientId = connection?.requester_id === user.id ? connection.recipient_id : connection?.requester_id;
    if (recipientId) {
      const body = `${profile?.display_name ?? "Someone"} shared something with you on Kindred`;
      await dispatchNotification({
        userId: recipientId,
        type: "direct_message",
        body,
        url: `/messages/${connectionId}`,
        entityType: "message",
        entityId: connectionId,
      });
    }

    toast.success("Shared to messages");
    setOpen(false);
  };

  const shareToChannel = async (channelId: string) => {
    if (!user?.id) return;

    await supabase.from("posts").insert({
      channel_id: channelId,
      user_id: user.id,
      content: encodeKindredShareContent(note, payload),
      photo_urls: [],
    } as any);
    toast.success("Shared to channel");
    setOpen(false);
  };

  const shareToRoom = async (roomId: string) => {
    if (!user?.id) return;

    await supabase.from("chat_room_messages" as any).insert({
      room_id: roomId,
      user_id: user.id,
      content: encodeKindredShareContent(note, payload),
      reply_to_id: null,
    } as any);
    toast.success("Shared to chat room");
    setOpen(false);
  };

  return (
    <>
      {iconOnly ? (
        <AppIconButton label={triggerLabel} tone={triggerTone} size={triggerSize} onPress={() => setOpen(true)}>
          {triggerContent ?? triggerLabel}
        </AppIconButton>
      ) : (
        <AppButton type="button" tone={triggerTone} size={triggerSize} onPress={() => setOpen(true)}>
          {triggerContent ?? triggerLabel}
        </AppButton>
      )}
      <AppBottomSheetSurface
        open={open}
        onOpenChange={setOpen}
        title="Share"
        description="Choose whether to share through your device or inside Kindred."
        maxWidthClassName="max-w-2xl"
      >
        <div className="max-h-[calc(100dvh-10rem)] space-y-5 overflow-y-auto overscroll-contain pr-1">
          <AppCard>
            <AppCardBody>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1 space-y-1">
                  <div>
                    <AppText className="font-semibold" fontSize="$sm" lineHeight="$sm">Share outside Kindred</AppText>
                  </div>
                  <div>
                    <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md">
                      Send by WhatsApp, Facebook, Messages, email, or copy the link.
                    </AppText>
                  </div>
                </div>
                <AppButton size="sm" onPress={() => void shareOutsideKindred()}>
                  Share
                </AppButton>
              </div>
            </AppCardBody>
          </AppCard>

          <AppSectionHeader title="Share inside Kindred" />

          <AppFieldGroup label="Add a message (optional)">
            <AppTextArea
              aria-label="Add a message (optional)"
              value={note}
              onChangeText={setNote}
              placeholder="Why are you sharing this?"
              maxLength={500}
              minHeight={84}
              rows={3}
            />
          </AppFieldGroup>

          <AppCard>
            <AppCardBody>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1 space-y-1">
                  <div>
                    <AppText className="font-semibold" fontSize="$sm" lineHeight="$sm">Post to Kinfeed</AppText>
                  </div>
                  <div>
                    <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md">
                      Open the Kinfeed composer with this reference card attached.
                    </AppText>
                  </div>
                </div>
                <AppButton size="sm" onPress={shareToKinfeed}>
                  Open
                </AppButton>
              </div>
            </AppCardBody>
          </AppCard>

          <AppSection gap="$xs">
            <AppSectionHeader title="Message threads" />
            <div className="space-y-2">
              {loading ? (
                <AppCard>
                  <AppCardBody py="$sm">
                    <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md">Loading share targets...</AppText>
                  </AppCardBody>
                </AppCard>
              ) : conversations.length ? (
                conversations.map((conversation) => (
                  <button
                    key={conversation.id}
                    onClick={() => void shareToConversation(conversation.id)}
                    className="w-full text-left"
                  >
                    <AppCard>
                      <AppCardBody py="$sm">
                        <div className="flex items-center justify-between gap-3">
                          <AppText className="font-medium" fontSize="$sm" lineHeight="$sm">{conversation.label}</AppText>
                          <AppText color="$primary" fontSize="$xs" lineHeight="$xs">Share</AppText>
                        </div>
                      </AppCardBody>
                    </AppCard>
                  </button>
                ))
              ) : (
                <AppCard>
                  <AppCardBody py="$sm">
                    <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md">No message threads yet.</AppText>
                  </AppCardBody>
                </AppCard>
              )}
            </div>
          </AppSection>

          <AppSection gap="$xs">
            <AppSectionHeader title="Channels" />
            <div className="space-y-2">
              {!channels.length ? (
                <AppCard>
                  <AppCardBody py="$sm">
                    <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md">Join a channel to share into it.</AppText>
                  </AppCardBody>
                </AppCard>
              ) : null}
              {channels.map((channel) => (
                <button
                  key={channel.id}
                  onClick={() => void shareToChannel(channel.id)}
                  className="w-full text-left"
                >
                  <AppCard>
                    <AppCardBody py="$sm">
                      <div className="flex items-center justify-between gap-3">
                        <AppText className="truncate font-medium" fontSize="$sm" lineHeight="$sm">
                          {channel.emoji} {channel.name}
                        </AppText>
                        <AppText color="$primary" fontSize="$xs" lineHeight="$xs">Share</AppText>
                      </div>
                    </AppCardBody>
                  </AppCard>
                </button>
              ))}
            </div>
          </AppSection>

          <AppSection gap="$xs">
            <AppSectionHeader title="Chat rooms" />
            <div className="space-y-2">
              {rooms.length ? (
                rooms.map((room) => (
                  <button
                    key={room.id}
                    onClick={() => void shareToRoom(room.id)}
                    className="w-full text-left"
                  >
                    <AppCard>
                      <AppCardBody py="$sm">
                        <div className="flex items-center justify-between gap-3">
                          <AppText className="truncate font-medium" fontSize="$sm" lineHeight="$sm">{room.name}</AppText>
                          <AppText color="$primary" fontSize="$xs" lineHeight="$xs">Share</AppText>
                        </div>
                      </AppCardBody>
                    </AppCard>
                  </button>
                ))
              ) : (
                <AppCard>
                  <AppCardBody py="$sm">
                    <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md">You are not in any chat rooms yet.</AppText>
                  </AppCardBody>
                </AppCard>
              )}
            </div>
          </AppSection>
        </div>
      </AppBottomSheetSurface>
    </>
  );
};

export default KindredShareSheet;
